import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import Row from 'react-bootstrap/lib/Row';
import Col from 'react-bootstrap/lib/Col';
import Label from 'react-bootstrap/lib/Label';
import Button from 'react-bootstrap/lib/Button';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';

// Components
import InboxItem from './InboxItem';
import Loader from '../Loader';
import history from '../../core/history';

// Locale
import messages from '../../locale/messages';

// Styles
import s from './Inbox.css';
import bt from '../../components/commonStyle.css';

const UnreadMessages = ({ userId, loading, getUnreadThreads }) => {
  const intl = useIntl();

  const renderLabel = (status, noStyle) => {
    let label, style;
    switch (status) {
      case 'inquiry':
        label = <FormattedMessage {...messages.messageStatus1} />;
        style = 'info';
        break;
      case 'preApproved':
        label = <FormattedMessage {...messages.messageStatus2} />;
        style = 'primary';
        break;
      case 'declined':
        label = <FormattedMessage {...messages.messageStatus3} />;
        style = 'danger';
        break;
      case 'approved':
        label = <FormattedMessage {...messages.messageStatus4} />;
        style = 'success';
        break;
      case 'pending':
        label = <FormattedMessage {...messages.messageStatus5} />;
        style = 'warning';
        break;
      case 'cancelledByHost':
        label = <FormattedMessage {...messages.messageStatus6} />;
        style = 'danger';
        break;
      case 'cancelledByGuest':
        label = <FormattedMessage {...messages.messageStatus7} />;
        style = 'danger';
        break;
      case 'intantBooking':
      case 'instantBooking':
      case 'confirmed':
        label = <FormattedMessage {...messages.messageStatus8} />;
        style = 'success';
        break;
      case 'expired':
        label = <FormattedMessage {...messages.messageStatus9} />;
        style = 'danger';
        break;
      case 'requestToBook':
        label = <FormattedMessage {...messages.messageStatus10} />;
        style = 'primary';
        break;
      case 'completed':
        label = <FormattedMessage {...messages.inboxCompleted} />;
        style = 'success';
        break;
      default:
        return null;
    }

    return noStyle ? label : <Label bsStyle={style}>{label}</Label>;
  };

  if (loading) {
    return <Loader type="text" />;
  }

  if (getUnreadThreads?.length > 0) {
    return (
      <div className={cx(s.progressContainerResponsive)}>
        <Row>
          <Col xs={12}>
            <ul className={cx(s.listLayout, 'listLayoutArbic')}>
              {getUnreadThreads.map((item, index) => {
                const type = userId === item.host ? 'host' : 'guest';

                if (item.threadItemUnread && item.guestProfile && item.hostProfile) {
                  const profile =
                    type === 'host' ? item.guestProfile : item.hostProfile;

                  return (
                    <InboxItem
                      key={index}
                      threadId={item.id}
                      type={type}
                      profileId={profile.profileId}
                      picture={profile.picture}
                      displayName={profile.firstName}
                      content={
                        item.threadItemUnread.content ??
                        renderLabel(item.threadItemUnread.type, true)
                      }
                      createdAt={item.threadItemUnread.createdAt}
                      city={item.listData.city}
                      state={item.listData.state}
                      country={item.listData.country}
                      startDate={item.threadItemUnread.startDate}
                      endDate={item.threadItemUnread.endDate}
                      status={renderLabel(item.threadItemUnread.type)}
                      sentBy={item.threadItemUnread.sentBy}
                      read={item.threadItemUnread.isRead}
                      isPhotoMigrated={profile?.isMigrated || false}
                    />
                  );
                } else {
                  return <li key={index} />;
                }
              })}
            </ul>

            {getUnreadThreads.length > 4 && (
              <div className={cx(s.loadMore)}>
                <Button
                  onClick={() => history.push('/inbox')}
                  className={cx(s.btn, bt.btnPrimary, s.loadMoreBtn)}
                >
                  <FormattedMessage {...messages.loadMore} />...
                </Button>
              </div>
            )}
          </Col>
        </Row>
      </div>
    );
  }

  return <div />;
};

UnreadMessages.propTypes = {
  userId: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  getUnreadThreads: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    listData: PropTypes.shape({
      city: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      country: PropTypes.string.isRequired,
    }),
    guestProfile: PropTypes.shape({
      profileId: PropTypes.number.isRequired,
      picture: PropTypes.string,
      displayName: PropTypes.string.isRequired,
    }),
    hostProfile: PropTypes.shape({
      profileId: PropTypes.number.isRequired,
      picture: PropTypes.string,
      displayName: PropTypes.string.isRequired,
    }),
    threadItemUnread: PropTypes.shape({
      type: PropTypes.string.isRequired,
      content: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      isRead: PropTypes.bool.isRequired,
      sentBy: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
    }),
  })),
};

UnreadMessages.defaultProps = {
  loading: true,
  getUnreadThreads: [],
};

export default withStyles(s)(UnreadMessages);
